import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { vendorDepositApi } from "../../api/vendorDepositApi";
import { toast } from "react-toastify";
import { useUser } from "../../hooks/useUser";
import usePath from "../../hooks/usePath";
import useFileUploader from "../../hooks/useFileUploader";
import SafeImage from "../../components/common/SafeImage";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

/**
 * AddVendorDepositCash Component
 * Form for creating/editing vendor petty cash deposits with admin-only access
 * Handles form validation, file uploads, and balance calculations
 */
const AddVendorDepositCash = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { uploadImage } = useFileUploader();

  const [isEditMode, setIsEditMode] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state for vendor deposit data including petty cash, expense, balance
  const [formData, setFormData] = useState({
    vendor_id: "",
    petty_cash: "",
    expense: "",
    balance: "0",
    notes: "",
    deposit_date: new Date().toISOString().split("T")[0],
    receipt_number: "",
    receipt_image: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [receiptImageFile, setReceiptImageFile] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState(" ");


  // Load initial data including vendors and existing deposit for editing
  const loadInitialData = useCallback(async () => {
    if (user?.role !== "employee" && user?.role !== "admin") {
      setError("You are not authorized to perform this action.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const vendorsResponse = await vendorDepositApi.getVendors();
      if (vendorsResponse.success) {
        setVendors(vendorsResponse.data);
      } else {
        throw new Error(vendorsResponse.message || "Failed to load vendors");
      }

      if (id) {
        setIsEditMode(true);
        const depositResponse = await vendorDepositApi.getDeposit(id);
        if (depositResponse.success) {
          const d = depositResponse.data;
          setCurrencySymbol(d.currency_symbol || " ");

          setFormData({
            vendor_id: d.vendor_id,
            petty_cash: d.petty_cash,
            expense: d.expense,
            balance: d.balance,
            notes: d.notes || "",
            deposit_date: d.deposit_date.split("T")[0],
            receipt_number: d.receipt_number || "",
            receipt_image: d.receipt_image || "",
          });
          //  ADD THIS EXACTLY HERE
          const selectedVendor = vendorsResponse.data.find(
            (v) => String(v.vendor_id) === String(d.vendor_id)
          );
          setCurrencySymbol(selectedVendor?.currency_symbol || " ");

          setImagePreview(d.receipt_image);
        } else {
          throw new Error(
            depositResponse.message || "Failed to load deposit data",
          );
        }
      }
    } catch (err) {
      setError(err.message || "Could not load data. Please try again later.");
      toast.error(err.message || "Could not load data.");
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Auto-calculate balance based on petty cash and expense values
  useEffect(() => {
    const pettyCash = parseFloat(formData.petty_cash) || 0;
    const expense = parseFloat(formData.expense) || 0;
    const balance = pettyCash - expense;
    setFormData((prev) => ({ ...prev, balance: balance.toFixed(2) }));
  }, [formData.petty_cash, formData.expense]);

  // Handle form input changes including file uploads
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "receipt_image") {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(files[0]);
      setReceiptImageFile(files[0]);
    } else {
      // currency symbol update (ONLY ADD THIS PART)
      if (name === "vendor_id") {
        const selectedVendor = vendors.find(
          (v) => String(v.vendor_id) === String(value)
        );
        setCurrencySymbol(selectedVendor?.currency_symbol || "");
      }
      // Handle empty expense field
      const finalValue = name === "expense" && value === "" ? "0" : value;
      setFormData((prevState) => ({ ...prevState, [name]: finalValue }));
    }
  };

  // Submit form data to create or update vendor deposit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vendor_id) {
      toast.error("Please select a vendor.");
      return;
    }

    setSubmitting(true);

    try {
      const data = new FormData();

      // ✅ If expense should be 0 always (field hidden)
      const submitData = {
        ...formData,
        expense: "0",
      };

      // ✅ Append normal fields (skip receipt_image string if any)
      Object.entries(submitData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        // IMPORTANT: do not send receipt_image as URL string
        if (key === "receipt_image") return;

        data.append(key, String(value));
      });

      // ✅ If user selected a NEW image file, send the file directly
      // (this matches backend: receipt_image must be an image file)
      if (receiptImageFile) {
        const uploadedPath = usePath(
          await uploadImage(receiptImageFile, "receipts", receiptImageFile.name)
        );
        data.append("receipt_image", uploadedPath); // ✅ string
      }


      // ✅ Edit mode: Laravel expects POST + _method=PUT for multipart/form-data
      let response;
      if (isEditMode) {
        data.append("_method", "PUT");
        response = await vendorDepositApi.updateDeposit(id, data);
      } else {
        response = await vendorDepositApi.createDeposit(data);
      }

      if (response?.success) {
        toast.success(`Vendor deposit ${isEditMode ? "updated" : "added"} successfully!`);
        navigate("/vendordepositcash");
        return;
      }

      toast.error(
        response?.message || `Failed to ${isEditMode ? "update" : "save"} vendor deposit.`
      );

      if (response?.errors) {
        Object.values(response.errors).forEach((err) => toast.error(err?.[0] || String(err)));
      }
    } catch (err) {
      console.error("API Error:", err);
      toast.error(err?.message || "An unknown error occurred.");

      if (err?.errors) {
        Object.values(err.errors).forEach((e) => toast.error(e?.[0] || String(e)));
      }
    } finally {
      setSubmitting(false);
    }
  };


  if (loading)
    return (
      <div className="p-4">
        <h4 className="placeholder-glow mb-4">
          <span className="placeholder placeholder-wave col-6 rounded-2"></span>
        </h4>
        <form>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-3 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-6">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-4 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-6">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-4 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-6">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-5 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-6">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-5 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-12">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-4 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "38px" }}
              ></div>
            </div>
            <div className="col-md-12">
              <label className="form-label placeholder-glow">
                <span className="placeholder placeholder-wave col-3 rounded-2"></span>
              </label>
              <div
                className="placeholder placeholder-wave col-12 rounded-3"
                style={{ height: "80px" }}
              ></div>
            </div>
            <div className="col-12">
              <hr className="my-3" />
              <div className="d-flex gap-3 justify-content-end">
                <span
                  className="placeholder placeholder-wave col-2 rounded-3"
                  style={{ height: "38px" }}
                ></span>
                <span
                  className="placeholder placeholder-wave col-2 rounded-3"
                  style={{ height: "38px" }}
                ></span>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  if (error)
    return (
      <div className="alert alert-danger text-center m-4">
        <h5>Error</h5>
        <p>{error}</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );

  return (
    <div className="p-4">
      <h2 className="mb-4">
        {isEditMode ? "Edit" : "Add"} Vendor Deposit Cash
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">
              Vendor <span className="text-danger">*</span>
            </label>
            <select
              className="form-select border-2"
              name="vendor_id"
              value={formData.vendor_id}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select a vendor
              </option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.vendor_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">
              Petty Cash <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">{currencySymbol}</span>
              <input
                type="number"
                step="0.01"
                className="form-control border-2"
                name="petty_cash"
                value={formData.petty_cash}
                onChange={handleChange}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Receipt Number</label>
            <input
              type="text"
              className="form-control border-2"
              name="receipt_number"
              value={formData.receipt_number}
              onChange={handleChange}
              placeholder="e.g., RCPT-12345"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">
              Deposit Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control border-2"
              name="deposit_date"
              value={formData.deposit_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-12 row g-3">
            <label className="form-label">Receipt Image</label>
            <input
              type="file"
              className="form-control border-2"
              name="receipt_image"
              onChange={handleChange}
              accept="image/*"
            />

            {isEditMode && imagePreview && !receiptImageFile && (
              <div className="mt-2">
                <small>Current Image: </small>
                <a
                  href={imagePreview}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {imagePreview}
                </a>
              </div>
            )}
            <small className="form-text text-muted">
              Supported formats: JPG, PNG, GIF. Max size: 2MB.
            </small>

            {imagePreview && (
              <SafeImage
                src={imagePreview}
                alt="Receipt Image Preview"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              />
            )}
          </div>

          <div className="col-md-12">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control border-2"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional notes"
              rows="3"
            />
          </div>

          <div className="col-12">
            <hr className="my-3" />
            <div className="d-flex gap-3 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                onClick={() => navigate("/vendordepositcash")}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={submitting}
              >
                {submitting
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                    ? "Update Deposit"
                    : "Save Deposit"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddVendorDepositCash;
